'use client';

import { Loading } from '@/components/loading';
import { Button } from '@/components/ui/button';
import { DataTableFilter, FilterConfig } from '@/components/ui/filter';
import { DataTablePagination } from '@/components/ui/pagination';
import { SearchInput } from '@/components/ui/search';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import {
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  OnChangeFn,
  PaginationState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { ReactNode } from 'react';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[] | undefined;
  totalCount?: number;
  pagination: PaginationState;
  onPaginationChange: OnChangeFn<PaginationState>;
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  filters?: FilterConfig[];
  columnFilters: ColumnFiltersState;
  onColumnFiltersChange: OnChangeFn<ColumnFiltersState>;
  showSearch?: boolean;
  searchPlaceholder?: string;
  isLoading?: boolean;
  tableRowClickable?: (row: TData) => boolean;
  onTableRowClick?: (row: TData) => void;
  addButton?: ReactNode;
}

export function DataTable<TData, TValue>({
  columns,
  data = [],
  totalCount,
  pagination,
  onPaginationChange,
  sorting,
  onSortingChange,
  columnFilters,
  onColumnFiltersChange,
  showSearch = false,
  searchPlaceholder = '',
  filters = [],
  isLoading = false,
  tableRowClickable = () => false,
  onTableRowClick = () => {},
  addButton,
}: DataTableProps<TData, TValue>) {
  const pageCount = totalCount ? Math.ceil(totalCount / pagination.pageSize) : -1;

  const table = useReactTable({
    data,
    columns,
    pageCount,
    state: {
      pagination,
      sorting,
      columnFilters,
    },
    manualPagination: true,
    onPaginationChange,
    manualSorting: true,
    onSortingChange,
    manualFiltering: true,
    onColumnFiltersChange,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-4">
      <div className={cn('flex justify-between items-center')}>
        <div className="flex gap-3">
          {showSearch && (
            <div className="min-w-[14.375rem]">
              <SearchInput
                searchPlaceholder={searchPlaceholder}
                initialValue={(columnFilters.find((f) => f.id === 'search')?.value as string) ?? ''}
                onColumnFiltersChange={onColumnFiltersChange}
                onPaginationChange={onPaginationChange}
              />
            </div>
          )}
        </div>
        <div className="flex gap-3">
          {filters.length > 0 && (
            <DataTableFilter
              columnFilters={columnFilters}
              setColumnFilters={onColumnFiltersChange}
              filters={filters}
              onPaginationChange={onPaginationChange}
            />
          )}
          {addButton && <div className="flex items-center">{addButton}</div>}
        </div>
      </div>
      <Table className={cn('table-fixed w-full')}>
        <TableHeader className={cn('bg-secondary-20')}>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead className={cn('border border-neutral-25', header.column.columnDef.meta?.className)} key={header.id}>
                  {header.isPlaceholder ? null : (
                    <div className="flex gap-2 ">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <Button
                          variant="tableHeader"
                          className="p-0 h-auto"
                          onClick={() => header.column.toggleSorting(header.column.getIsSorted() === 'asc')}
                        >
                          {header.column.getIsSorted() === 'asc' ? (
                            <ArrowUp className="h-4 w-4" />
                          ) : header.column.getIsSorted() === 'desc' ? (
                            <ArrowDown className="h-4 w-4" />
                          ) : (
                            <ArrowUpDown className="h-4 w-4 text-primary" />
                          )}
                        </Button>
                      )}
                    </div>
                  )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody className="border-t-2">
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className={cn('h-24 text-center')}>
                <Loading />
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                className={tableRowClickable(row.original) ? cn('cursor-pointer hover:bg-neutral-100') : ''}
                onClick={() => {
                  tableRowClickable(row.original) ? onTableRowClick(row.original) : undefined;
                }}
                data-state={row.getIsSelected() && 'selected'}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className={cn('border border-neutral-25', cell.column.columnDef.meta?.className)}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className={cn('h-24 text-center')}>
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <DataTablePagination table={table} isLoading={isLoading} />
    </div>
  );
}
